<template>
  <div class="store-select mb-5 row">
    <!-- START : Store Details -->
    <div class="store-select store-select__store-name col-lg-8">
      <!-- START : Store Details - store name and distance info -->
      <div class="row no-gutters align-items-baseline">
        <button
          class="btn btn-link text-decoration-none text-left font-size-lg p-0 text-dark border-0 col-6"
          @click="selectStore(store)"
        >
          <h2>{{ store.name }}</h2>
        </button>
        <div v-if="store.distance" class="text-md-left text-right col-6">
          {{ distanceInMiles }} {{ $t('milesAbbr') }} / {{ distanceInKm }}
          {{ $t('kilometersAway') }}
        </div>
      </div>
      <!-- END : Store Details - store name and distance info -->

      <!-- START : Store contact info -->
      <div>
        <div v-if="!getLocationAddress && !getLocationPhone && !getLocationEmail">
          <p>{{ $t('noContactInfoAvailable') }}</p>
        </div>

        <!-- START : Store address details -->
        <div v-if="getLocationAddress">
          <div class="store-select store-select__contact-info">
            <a :href="mapStoreAddress" class="underline-link-from-center text-dark" target="_blank">
              <div v-if="getLocationAddress.street1 || getLocationAddress.street2">
                <span v-if="getLocationAddress.street1">{{ getLocationAddress.street1 }}</span>
                <span v-if="getLocationAddress.street1"
                  ><br />{{ getLocationAddress.street2 }}</span
                >
              </div>
              <span class="locality" v-if="getLocationAddress.city"
                >{{ getLocationAddress.city }}
              </span>
              <span class="region" v-if="getLocationAddress.state"
                >{{ getLocationAddress.state }}
              </span>
              <span class="postal-code" v-if="getLocationAddress.postal">{{
                getLocationAddress.postal
              }}</span>
            </a>
          </div>
        </div>
        <!-- END : Store address details -->

        <!-- START : Store phone details -->
        <div v-if="getLocationPhone">
          <div class="store-select store-select__contact-info">
            <font-awesome-icon class="mr-2" icon="phone" :title="$t('phoneNumber')" />
            <a
              :href="`tel:${getLocationPhone.phone}`"
              class="underline-link-from-center text-dark"
              >{{ getLocationPhone.phone }}</a
            >
          </div>
        </div>
        <!-- END : Store phone details -->

        <!-- START : Store email details -->
        <div v-if="getLocationEmail">
          <div class="store-select store-select__contact-info">
            <font-awesome-icon class="mr-2" icon="envelope" :title="$t('emailAddress')" />
            <a
              :href="`mailto:${getLocationEmail.email}?subject='Customer question'`"
              class="underline-link-from-center text-dark"
              >{{ getLocationEmail.email }}</a
            >
          </div>
        </div>
        <!-- END : Store phone details -->
      </div>
      <!-- END : Store contact info -->
    </div>
    <!-- END : STORE DETAILS -->

    <!-- START : Store selection details based on isAvailableOnline and isCurrentStore -->
    <div class="col-lg-4 location-mobile--view mt-3 mt-lg-0" v-if="isMultiStore">
      <span class="store-select store-select__store-info" v-if="!isAvailableOnline">
        <i class="wc-icon wc-icon-exclamation-circle"></i>
        {{ $t('notAvailableOnline') }}
      </span>
      <template v-if="isAvailableOnline">
        <button
          class="store-select mr-3 mr-lg-0 store-select__store-info btn btn-primary text-white py-1 font-weight-bold"
          v-if="isCurrentStore"
          data-current="current"
          disabled="true"
        >
          {{ $t('currentlySelected') }}
        </button>
        <a
          v-else
          class="store-select shop-store-link d-block mr-3 mr-lg-0 store-select__store-info btn btn-primary mb-0 py-1 text-white font-weight-bold"
          :href="`/s/${store.id}`"
          data-shop-btn="shop-button"
        >
          {{ $t('shopStore') }}
        </a>
        <div
          class="store-select store-select__store-info mt-3 text-center mb-0 font-weight-bold"
          v-if="isHomeStore"
        >
          <font-awesome-icon :icon="['fa', 'house']" class="mr-1" :title="$t('homeStore')" />
          {{ $t('homeStore') }}
        </div>
        <div class="store-select store-select__store-info mt-3 text-center" v-else>
          <button
            class="btn btn-link underline-link-from-center font-weight-bold mb-0 text-primary"
            @click="setHomeStore(store)"
          >
            {{ $t('makeHomeStore') }}
          </button>
        </div>
      </template>
    </div>
    <!-- END : Store selection details based on isAvailableOnline and isCurrentStore -->
  </div>
</template>

<script>
import UserMixin from '@/modules/user/mixins/UserMixin';
import { getOneLineAddress } from '@/utils';
import { MAP_CONSTANTS } from '@/constants/AppConstants';

export default {
  props: {
    store: {
      type: Object,
      required: true,
    },
    isCurrentStore: {
      type: Boolean,
      required: true,
    },
    isHomeStore: {
      type: Boolean,
      required: true,
    },
    isSelected: {
      type: Boolean,
      required: true,
    },
  },
  mixins: [UserMixin],
  methods: {
    /**
     * Method to get address for Google maps api
     */
    oneLineAddress(address) {
      return getOneLineAddress(address);
    },
    /*
     * Emits this store to SelectStore component
     */
    selectStore(store) {
      this.$emit('selectStore', store);
    },
    /**
     * On selecting a home store, make it as home store and navigate to the home store
     */
    async setHomeStore(store) {
      await this.setUserHomeStore(store.id);
      if (this.$configuration.store.id !== store.id) this.$router.push(`/redirect-to/${store.id}`);
    },
  },
  computed: {
    /**
     * Address link for Google api
     */
    mapStoreAddress() {
      return `${MAP_CONSTANTS.GOOGLE_MAP_API}?q=${this.oneLineAddress(this.getLocationAddress)}`;
    },
    /**
     * Check if there are multiple stores present
     * If there is only one store which is default to home, then show no options
     */
    isMultiStore() {
      return this.$configuration.multistore;
    },

    getLocationAddress() {
      if (this.store.addresses && this.store.addresses.length) {
        return this.store.addresses.find(address => address.location);
      }

      return null;
    },
    getLocationPhone() {
      if (this.store.phones && this.store.phones.length) {
        return this.store.phones.find(phone => phone.locationPhone);
      }

      return null;
    },
    getLocationEmail() {
      if (this.store.emails && this.store.emails.length) {
        return this.store.emails.find(email => email.location);
      }

      return null;
    },
    isAvailableOnline() {
      return this.store.webStore;
    },
    distanceInMiles() {
      return (this.store.distance * 0.000621371).toFixed(1);
    },
    distanceInKm() {
      return (this.store.distance * 0.001).toFixed(1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

@include media-breakpoint-up(lg) {
  button {
    &.store-select__store-info {
      width: 100%;
    }
  }
}
@include media-breakpoint-down(md) {
  .location-mobile--view {
    display: flex;
  }
}
.store-select__contact-info {
  display: flex;
  svg {
    position: relative;
    top: 5px;
    left: 0;
  }
}
.store-select:last-child {
  margin-bottom: 0 !important;
}
</style>
